/**
 * Assign-users drawer, live assignments, and bulk row selection for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own the DHTMLX chart engine or save/discard session sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import { syncAssignSelectCheckboxes } from '../../../gantt/ganttAssignSelectPaint';
import { fetchProjectAssignUsers } from '../../../api/projectUsersApi';
import { notifications } from '@mantine/notifications';
import { fetchUserAssignments } from '../../../api/userAssignmentApis';
import { resolveAssignmentsForTask, formatTaskAssigneesSummary, buildAssignTargetChipsFromTaskIds } from '../components/assignments/assignDisplayUtils';
import { resolveTaskName, normalizeTaskType } from '../../../utils/utils';
import { flattenAssignLocationOptions, buildLocationPathById } from '../components/assignments/flattenAssignLocations';
import { createMockAssignments } from '../components/assignments/assignUsersMockData';
import type { PlanningUserAssignment } from '../../../api/userAssignmentApis';
import type { AssignUsersDrawerMode, AssignUsersScope, AssignTargetChip, AssignUserOption, AssignTaskOption } from '../components/assignments/assignUsersTypes';

function assignmentSignature(row: PlanningUserAssignment): string {
  return [
    row.id,
    row.scopeType,
    row.locationId ?? '',
    row.taskId ?? '',
    row.userId,
    row.userName ?? '',
    row.role,
    row.status,
  ].join('\u001f');
}

/** Keep the same array when a refetch matches what the grid already shows. */
function commitLiveAssignments(
  setRows: Dispatch<SetStateAction<PlanningUserAssignment[]>>,
  rows: PlanningUserAssignment[],
) {
  setRows((prev) => {
    if (prev === rows) return prev;
    if (prev.length !== rows.length) return rows;
    const seen = new Set(prev.map(assignmentSignature));
    for (const row of rows) {
      if (!seen.has(assignmentSignature(row))) return rows;
    }
    return prev;
  });
}

export function useGanttAssignments(p: Record<string, any>) {
  const {
    searchParams,
    locationTree,
    effectiveLocationIdByTaskId,
    dhtmlxTasks,
    selectedTaskId,
    locationIdToName,
    projectId,
  } = p;
  const store = p.store as ITaskStore;

const assignMockEnabled = searchParams.get('assignMock') === '1';
const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
const [assignDrawerMode, setAssignDrawerMode] = useState<AssignUsersDrawerMode>('live');
const [assignPresentation, setAssignPresentation] = useState<'shell' | 'modal'>('shell');
const [assignTargetsLocked, setAssignTargetsLocked] = useState(false);
const [assignInitialScope, setAssignInitialScope] = useState<AssignUsersScope>('location');
const [assignInitialTaskTargets, setAssignInitialTaskTargets] = useState<AssignTargetChip[]>(
  [],
);
const [mockAssignments, setMockAssignments] = useState<PlanningUserAssignment[]>(() =>
  createMockAssignments(),
);
const [liveAssignments, setLiveAssignments] = useState<PlanningUserAssignment[]>([]);
const [assignUserOptions, setAssignUserOptions] = useState<AssignUserOption[]>([]);
const [assignUsersLoading, setAssignUsersLoading] = useState(false);
const [assignAssignmentsLoading, setAssignAssignmentsLoading] = useState(false);
const assignMockAutoOpenedRef = useRef(false);
/** App-owned multi-select for Assign users (separate from Task Details selection). */
const [selectedAssignTaskIds, setSelectedAssignTaskIds] = useState<Set<string>>(
  () => new Set(),
);
const selectedAssignTaskIdsRef = useRef(selectedAssignTaskIds);
/** Keep ref in sync on render; setters also write immediately for paint/templates. */
selectedAssignTaskIdsRef.current = selectedAssignTaskIds;

const assignLocationOptions = useMemo(
  () => flattenAssignLocationOptions(locationTree ?? []),
  [locationTree],
);

const locationPathById = useMemo(
  () => buildLocationPathById(assignLocationOptions),
  [assignLocationOptions],
);

const assignTaskCatalog = useMemo<AssignTaskOption[]>(() => {
  return store.getTasks().map((t) => {
    const id = String(t.id);
    const loc =
      String(t.locationId ?? '').trim() ||
      String(effectiveLocationIdByTaskId.get(id) ?? '').trim() ||
      null;
    const locPath = loc ? locationPathById.get(loc) : undefined;
    return {
      id,
      label: resolveTaskName(t) || id,
      locationId: loc,
      // Location hierarchy only — WBS parent names clutter the Assign drawer path.
      breadcrumb: locPath,
    };
  });
}, [store, dhtmlxTasks, effectiveLocationIdByTaskId, locationPathById]);

const buildSelectedTaskAssignChip = useCallback((): AssignTargetChip | null => {
  if (!selectedTaskId) return null;
  const task = store.getTask(selectedTaskId);
  if (!task) return null;
  const loc =
    String(task.locationId ?? '').trim() ||
    String(effectiveLocationIdByTaskId.get(String(selectedTaskId)) ?? '').trim();
  if (!loc) return null;

  const type = normalizeTaskType(task.taskType) ?? 'unknown';
  if (type === 'location') {
    const locOpt = assignLocationOptions.find((o) => o.value === loc);
    return {
      kind: 'location',
      id: loc,
      label: locOpt?.label || locationIdToName.get(loc) || loc,
      locationId: loc,
      breadcrumb: locOpt?.breadcrumb,
      sourceTaskIds: [String(selectedTaskId)],
    };
  }

  const catalogHit = assignTaskCatalog.find((t) => t.id === String(selectedTaskId));
  return {
    kind: 'task',
    id: String(selectedTaskId),
    label: resolveTaskName(task) || String(selectedTaskId),
    locationId: loc,
    breadcrumb: catalogHit?.breadcrumb,
    sourceTaskIds: [String(selectedTaskId)],
  };
}, [
  selectedTaskId,
  store,
  effectiveLocationIdByTaskId,
  assignTaskCatalog,
  assignLocationOptions,
  locationIdToName,
]);

const selectedTaskAssigneesSummary = useMemo(() => {
  if (!selectedTaskId) return '';
  const taskId = String(selectedTaskId);
  const locId =
    String(store.getTask(taskId)?.locationId ?? '').trim() ||
    String(effectiveLocationIdByTaskId.get(taskId) ?? '').trim();
  const rows = resolveAssignmentsForTask({
    taskId,
    effectiveLocationId: locId || null,
    assignments: liveAssignments,
    locationNameById: locationIdToName,
  });
  return formatTaskAssigneesSummary(rows, { maxNames: 4 });
}, [selectedTaskId, liveAssignments, store, effectiveLocationIdByTaskId, locationIdToName, dhtmlxTasks]);

const refreshLiveAssignments = useCallback(async () => {
  if (!projectId) {
    setLiveAssignments([]);
    return;
  }
  setAssignAssignmentsLoading(true);
  try {
    const rows = await fetchUserAssignments({ projectId }, { silent: true });
    commitLiveAssignments(setLiveAssignments, rows);
  } catch {
    // Silent — drawer / Tools can still open; user sees empty list.
  } finally {
    setAssignAssignmentsLoading(false);
  }
}, [projectId]);

const openAssignUsersDrawer = useCallback(
  (options?: {
    prefillSelectedTask?: boolean;
    mode?: AssignUsersDrawerMode;
    presentation?: 'shell' | 'modal';
    initialTargets?: AssignTargetChip[];
    targetsLocked?: boolean;
  }) => {
    const mode = options?.mode ?? (assignMockEnabled ? 'mock' : 'live');
    const fromTaskDetails = Boolean(options?.prefillSelectedTask);
    const bulkTargets = options?.initialTargets;
    setAssignDrawerMode(mode);
    setAssignPresentation(
      options?.presentation ?? (fromTaskDetails ? 'modal' : 'shell'),
    );
    setAssignTargetsLocked(
      options?.targetsLocked ?? fromTaskDetails,
    );
    if (mode === 'mock') {
      setMockAssignments((prev) => (prev.length > 0 ? prev : createMockAssignments()));
    }
    if (bulkTargets && bulkTargets.length > 0) {
      const hasTask = bulkTargets.some((t) => t.kind === 'task');
      setAssignInitialScope(hasTask ? 'task' : 'location');
      setAssignInitialTaskTargets(bulkTargets);
    } else if (fromTaskDetails) {
      const chip = buildSelectedTaskAssignChip();
      setAssignInitialScope(chip?.kind === 'location' ? 'location' : 'task');
      setAssignInitialTaskTargets(chip ? [chip] : []);
      if (!chip && selectedTaskId) {
        notifications.show({
          title: 'Missing location',
          message:
            'This task has no locationId, so it cannot be added as an assignment target yet.',
          color: 'orange',
        });
      }
    } else {
      setAssignInitialScope('location');
      setAssignInitialTaskTargets([]);
    }
    setAssignDrawerOpen(true);
  },
  [assignMockEnabled, buildSelectedTaskAssignChip, selectedTaskId],
);

const clearAssignSelection = useCallback(() => {
  const next = new Set<string>();
  selectedAssignTaskIdsRef.current = next;
  setSelectedAssignTaskIds(next);
}, []);

const toggleAssignTaskSelected = useCallback((taskId: string, selected: boolean) => {
  setSelectedAssignTaskIds((prev) => {
    const next = new Set(prev);
    if (selected) next.add(taskId);
    else next.delete(taskId);
    selectedAssignTaskIdsRef.current = next;
    return next;
  });
}, []);

useEffect(() => {
  const next = new Set<string>();
  selectedAssignTaskIdsRef.current = next;
  setSelectedAssignTaskIds(next);
}, [projectId]);

useEffect(() => {
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (selectedAssignTaskIdsRef.current.size === 0) return;
    if (assignDrawerOpen) return;
    clearAssignSelection();
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}, [assignDrawerOpen, clearAssignSelection]);

useEffect(() => {
  if (!projectId) {
    setLiveAssignments([]);
    setAssignUserOptions([]);
    return;
  }
  let cancelled = false;
  void fetchUserAssignments({ projectId }, { silent: true })
    .then((rows) => {
      if (!cancelled) commitLiveAssignments(setLiveAssignments, rows);
    })
    .catch(() => {
      /* ignore */
    });
  void fetchProjectAssignUsers({ silent: true })
    .then((users) => {
      if (!cancelled) setAssignUserOptions(users);
    })
    .catch(() => {
      /* ignore */
    });
  return () => {
    cancelled = true;
  };
}, [projectId]);

useEffect(() => {
  if (!assignDrawerOpen || assignDrawerMode !== 'live' || !projectId) return;
  let cancelled = false;
  setAssignUsersLoading(true);
  setAssignAssignmentsLoading(true);
  void Promise.all([
    fetchProjectAssignUsers({ silent: true }).catch(() => [] as AssignUserOption[]),
    fetchUserAssignments({ projectId }, { silent: true }).catch(
      () => [] as PlanningUserAssignment[],
    ),
  ]).then(([users, rows]) => {
    if (cancelled) return;
    setAssignUserOptions(users);
    commitLiveAssignments(setLiveAssignments, rows);
  }).finally(() => {
    if (!cancelled) {
      setAssignUsersLoading(false);
      setAssignAssignmentsLoading(false);
    }
  });
  return () => {
    cancelled = true;
  };
}, [assignDrawerOpen, assignDrawerMode, projectId]);

useEffect(() => {
  if (!assignMockEnabled || assignMockAutoOpenedRef.current) return;
  assignMockAutoOpenedRef.current = true;
  openAssignUsersDrawer({ mode: 'mock' });
}, [assignMockEnabled, openAssignUsersDrawer]);

  return {
    assignDrawerOpen,
    setAssignDrawerOpen,
    assignDrawerMode,
    setAssignDrawerMode,
    assignPresentation,
    setAssignPresentation,
    assignTargetsLocked,
    setAssignTargetsLocked,
    assignInitialScope,
    setAssignInitialScope,
    assignInitialTaskTargets,
    setAssignInitialTaskTargets,
    mockAssignments,
    setMockAssignments,
    liveAssignments,
    setLiveAssignments,
    assignUserOptions,
    setAssignUserOptions,
    assignUsersLoading,
    setAssignUsersLoading,
    assignAssignmentsLoading,
    setAssignAssignmentsLoading,
    selectedAssignTaskIds,
    setSelectedAssignTaskIds,
    assignMockEnabled,
    assignMockAutoOpenedRef,
    selectedAssignTaskIdsRef,
    assignLocationOptions,
    locationPathById,
    assignTaskCatalog,
    buildSelectedTaskAssignChip,
    selectedTaskAssigneesSummary,
    refreshLiveAssignments,
    openAssignUsersDrawer,
    clearAssignSelection,
    toggleAssignTaskSelected,
  };
}


/**
 * Filtered-leaf assign targets and checkbox selection.
 * Called after column filters exist; same module as useGanttAssignments.
 */
export function useGanttAssignBulk(p: Record<string, any>) {
  const {
    parentIdsWithChildren,
    targetsFocusTaskIds,
    criticalFocusTaskIds,
    searchFocusTaskIds,
    columnFilterFocusTaskIds,
    effectiveLocationIdByTaskId,
    dhtmlxTasks,
    calendarReady,
    ganttRef,
    assignLocationOptions,
    openAssignUsersDrawer,
    assignMockEnabled,
    assignDrawerOpen,
    assignTargetsLocked,
  } = p;
  const store = p.store as ITaskStore;
  const assignTaskCatalog = p.assignTaskCatalog as AssignTaskOption[];
  const selectedAssignTaskIds = p.selectedAssignTaskIds as Set<string>;
  const selectedAssignTaskIdsRef = p.selectedAssignTaskIdsRef as MutableRefObject<Set<string>>;
  const setSelectedAssignTaskIds = p.setSelectedAssignTaskIds as Dispatch<
    SetStateAction<Set<string>>
  >;

const assignFilterLeafTasks = useMemo<AssignTargetChip[]>(() => {
  if (!columnFilterFocusTaskIds) return [];
  const byId = new Map(assignTaskCatalog.map((t) => [t.id, t]));
  const chips: AssignTargetChip[] = [];
  for (const t of store.getTasks()) {
    const id = String(t.id);
    if (parentIdsWithChildren.has(id)) continue;
    if ((normalizeTaskType(t.taskType) ?? 'unknown') === 'location') continue;
    if (targetsFocusTaskIds && !targetsFocusTaskIds.has(id)) continue;
    if (criticalFocusTaskIds && !criticalFocusTaskIds.has(id)) continue;
    if (searchFocusTaskIds && !searchFocusTaskIds.has(id)) continue;
    if (!columnFilterFocusTaskIds.has(id)) continue;
    const catalog = byId.get(id);
    const loc =
      catalog?.locationId?.trim() ||
      String(t.locationId ?? '').trim() ||
      String(effectiveLocationIdByTaskId.get(id) ?? '').trim();
    chips.push({
      kind: 'task',
      id,
      label: catalog?.label || resolveTaskName(t) || id,
      locationId: loc,
      breadcrumb: catalog?.breadcrumb,
    });
  }
  return chips;
}, [
  store,
  dhtmlxTasks,
  parentIdsWithChildren,
  targetsFocusTaskIds,
  criticalFocusTaskIds,
  searchFocusTaskIds,
  columnFilterFocusTaskIds,
  effectiveLocationIdByTaskId,
  assignTaskCatalog,
]);

/** Task search catalog — excludes location-type (those belong under Locations). */
const assignTaskSearchCatalog = useMemo(
  () =>
    assignTaskCatalog.filter((entry) => {
      const task = store.getTask(entry.id);
      return (normalizeTaskType(task?.taskType) ?? 'unknown') !== 'location';
    }),
  [assignTaskCatalog, store, dhtmlxTasks],
);

/** Visible leaf tasks with a locationId — same set used by Select all / header state. */
const assignSelectableLeafIds = useMemo(() => {
  const leafIds = new Set<string>();
  let skipped = 0;
  for (const t of store.getTasks()) {
    const id = String(t.id);
    if (parentIdsWithChildren.has(id)) continue;
    if (targetsFocusTaskIds && !targetsFocusTaskIds.has(id)) continue;
    if (criticalFocusTaskIds && !criticalFocusTaskIds.has(id)) continue;
    if (searchFocusTaskIds && !searchFocusTaskIds.has(id)) continue;
    if (columnFilterFocusTaskIds && !columnFilterFocusTaskIds.has(id)) continue;
    const loc =
      String(t.locationId ?? '').trim() ||
      String(effectiveLocationIdByTaskId.get(id) ?? '').trim();
    if (!loc) {
      skipped += 1;
      continue;
    }
    leafIds.add(id);
  }
  return { leafIds, skipped };
}, [
  store,
  parentIdsWithChildren,
  targetsFocusTaskIds,
  criticalFocusTaskIds,
  searchFocusTaskIds,
  columnFilterFocusTaskIds,
  effectiveLocationIdByTaskId,
  dhtmlxTasks,
]);

/** Append currently visible selectable leaves to the assign selection (does not wipe prior picks). */
const selectVisibleLeavesForAssign = useCallback(() => {
  const next = new Set(selectedAssignTaskIdsRef.current);
  for (const id of assignSelectableLeafIds.leafIds) {
    next.add(id);
  }
  selectedAssignTaskIdsRef.current = next;
  setSelectedAssignTaskIds(next);
  if (assignSelectableLeafIds.skipped > 0) {
    notifications.show({
      title: 'Some tasks skipped',
      message: `${assignSelectableLeafIds.skipped} visible leaf task${
        assignSelectableLeafIds.skipped === 1 ? '' : 's'
      } have no locationId.`,
      color: 'orange',
    });
  }
}, [assignSelectableLeafIds]);

/** Remove only currently visible selectable leaves; keep selections outside the current view. */
const deselectVisibleLeavesForAssign = useCallback(() => {
  if (assignSelectableLeafIds.leafIds.size === 0) return;
  setSelectedAssignTaskIds((prev) => {
    let changed = false;
    const next = new Set(prev);
    for (const id of assignSelectableLeafIds.leafIds) {
      if (next.delete(id)) changed = true;
    }
    if (!changed) return prev;
    selectedAssignTaskIdsRef.current = next;
    return next;
  });
}, [assignSelectableLeafIds]);

/** Paint visible assign checkboxes without rebuilding ganttColumns / ReactGantt config. */
useEffect(() => {
  if (!calendarReady) return;
  const gantt = ganttRef.current?.instance as { $container?: HTMLElement } | undefined;
  const ganttRoot =
    gantt?.$container ??
    (document.querySelector('.fbt-gantt-root') as HTMLElement | null);
  if (!ganttRoot) return;
  syncAssignSelectCheckboxes(
    ganttRoot,
    selectedAssignTaskIds,
    assignSelectableLeafIds.leafIds,
  );
}, [calendarReady, selectedAssignTaskIds, assignSelectableLeafIds]);

const openAssignFromBulkSelection = useCallback(() => {
  const { chips, skippedNoLocation } = buildAssignTargetChipsFromTaskIds({
    taskIds: selectedAssignTaskIds,
    getTask: (id) => store.getTask(id) ?? null,
    effectiveLocationIdByTaskId,
    locationOptions: assignLocationOptions,
    taskCatalog: assignTaskCatalog,
    normalizeTaskType,
    resolveTaskName,
  });
  if (skippedNoLocation > 0) {
    notifications.show({
      title: 'Some tasks skipped',
      message: `${skippedNoLocation} selected task${skippedNoLocation === 1 ? '' : 's'} have no locationId.`,
      color: 'orange',
    });
  }
  if (chips.length === 0) {
    notifications.show({
      title: 'Nothing to assign',
      message: 'None of the selected tasks can be used as assign targets.',
      color: 'orange',
    });
    return;
  }
  openAssignUsersDrawer({
    initialTargets: chips,
    presentation: 'shell',
    mode: assignMockEnabled ? 'mock' : 'live',
  });
}, [
  selectedAssignTaskIds,
  store,
  effectiveLocationIdByTaskId,
  assignLocationOptions,
  assignTaskCatalog,
  openAssignUsersDrawer,
  assignMockEnabled,
]);

const assignSelectionTargets = useMemo(() => {
  if (!assignDrawerOpen || assignTargetsLocked) return [];
  return buildAssignTargetChipsFromTaskIds({
    taskIds: selectedAssignTaskIds,
    getTask: (id) => store.getTask(id) ?? null,
    effectiveLocationIdByTaskId,
    locationOptions: assignLocationOptions,
    taskCatalog: assignTaskCatalog,
    normalizeTaskType,
    resolveTaskName,
  }).chips;
}, [
  assignDrawerOpen,
  assignTargetsLocked,
  selectedAssignTaskIds,
  store,
  effectiveLocationIdByTaskId,
  assignLocationOptions,
  assignTaskCatalog,
  dhtmlxTasks,
]);

const assignSelectionTaskIds = useMemo(
  () => Array.from(selectedAssignTaskIds),
  [selectedAssignTaskIds],
);

/** locationId → location-type gantt row ids (for Locations tab ↔ checkbox sync). */
const locationTypeTaskIdsByLocationId = useMemo(() => {
  const map = new Map<string, string[]>();
  for (const t of store.getTasks()) {
    if ((normalizeTaskType(t.taskType) ?? 'unknown') !== 'location') continue;
    const id = String(t.id);
    const loc =
      String(t.locationId ?? '').trim() ||
      String(effectiveLocationIdByTaskId.get(id) ?? '').trim();
    if (!loc) continue;
    const list = map.get(loc);
    if (list) list.push(id);
    else map.set(loc, [id]);
  }
  return map;
}, [store, dhtmlxTasks, effectiveLocationIdByTaskId]);

const handleAssignSelectionTaskIdsChange = useCallback(
  (next: string[] | ((prev: string[]) => string[])) => {
    setSelectedAssignTaskIds((prevSet) => {
      const prevArr = Array.from(prevSet);
      const resolved = typeof next === 'function' ? next(prevArr) : next;
      const newSet = new Set(
        resolved.map((id) => String(id).trim()).filter(Boolean),
      );
      if (newSet.size === prevSet.size) {
        let same = true;
        for (const id of newSet) {
          if (!prevSet.has(id)) {
            same = false;
            break;
          }
        }
        if (same) return prevSet;
      }
      selectedAssignTaskIdsRef.current = newSet;
      return newSet;
    });
  },
  [],
);

const handleAssignSelectionClearScope = useCallback(
  (clearScope: 'location' | 'task') => {
    setSelectedAssignTaskIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        const task = store.getTask(id);
        if (!task) {
          // Orphans (missing store row): drop with Tasks clear; keep on Locations clear.
          if (clearScope === 'location') next.add(id);
          continue;
        }
        const isLoc = (normalizeTaskType(task.taskType) ?? 'unknown') === 'location';
        if (clearScope === 'location') {
          if (!isLoc) next.add(id);
        } else if (isLoc) {
          next.add(id);
        }
      }
      selectedAssignTaskIdsRef.current = next;
      return next;
    });
  },
  [store],
);

const handleAssignSelectionLocationIdRemove = useCallback(
  (locationId: string) => {
    const loc = String(locationId ?? '').trim();
    if (!loc) return;
    setSelectedAssignTaskIds((prev) => {
      const next = new Set(prev);
      // Prefer the precomputed index; also scan selection for safety.
      for (const id of locationTypeTaskIdsByLocationId.get(loc) ?? []) {
        next.delete(id);
      }
      for (const id of prev) {
        const task = store.getTask(id);
        if (!task) continue;
        if ((normalizeTaskType(task.taskType) ?? 'unknown') !== 'location') continue;
        const taskLoc =
          String(task.locationId ?? '').trim() ||
          String(effectiveLocationIdByTaskId.get(id) ?? '').trim();
        if (taskLoc === loc) next.delete(id);
      }
      selectedAssignTaskIdsRef.current = next;
      return next;
    });
  },
  [store, effectiveLocationIdByTaskId, locationTypeTaskIdsByLocationId],
);

/** Check location-type rows for these locationIds; return ids with no matching row. */
const handleAssignSelectionLocationIdsAdd = useCallback(
  (locationIds: string[]): string[] => {
    const unresolved: string[] = [];
    const toAdd: string[] = [];
    for (const raw of locationIds) {
      const loc = String(raw ?? '').trim();
      if (!loc) continue;
      const rowIds = locationTypeTaskIdsByLocationId.get(loc) ?? [];
      if (rowIds.length === 0) {
        unresolved.push(loc);
        continue;
      }
      for (const id of rowIds) toAdd.push(id);
    }
    if (toAdd.length > 0) {
      setSelectedAssignTaskIds((prev) => {
        const next = new Set(prev);
        for (const id of toAdd) next.add(id);
        selectedAssignTaskIdsRef.current = next;
        return next;
      });
    }
    return unresolved;
  },
  [locationTypeTaskIdsByLocationId],
);

  return {
    assignFilterLeafTasks,
    assignTaskSearchCatalog,
    assignSelectableLeafIds,
    selectVisibleLeavesForAssign,
    deselectVisibleLeavesForAssign,
    openAssignFromBulkSelection,
    assignSelectionTargets,
    assignSelectionTaskIds,
    locationTypeTaskIdsByLocationId,
    handleAssignSelectionTaskIdsChange,
    handleAssignSelectionClearScope,
    handleAssignSelectionLocationIdRemove,
    handleAssignSelectionLocationIdsAdd,
  };
}
